import { useQueryClient } from "@tanstack/react-query";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";
import {
  HubSpotForm,
  HubSpotFormsSettings,
  HubSpotFormsSettingsUpdate
} from "@/entities/hubspot/types";

const SETTINGS_KEY = "hubspot/settings";

const errorStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

// The forms endpoint answers 409 when HubSpot isn't connected, and 403 when
// the connection lacks the forms permission.
export const isHubSpotNotConnected = (error: unknown): boolean =>
  errorStatus(error) === 409;

export const isHubSpotFormsForbidden = (error: unknown): boolean =>
  errorStatus(error) === 403;

export const useHubSpotSettings =
  (): ExtendedUseQueryResult<HubSpotFormsSettings> => useAPI(SETTINGS_KEY);

export const useHubSpotForms = (): ExtendedUseQueryResult<HubSpotForm[]> =>
  useAPI("hubspot/forms", undefined, {
    select: (response: { forms: HubSpotForm[] }) => response.forms
  });

// One PUT takes the whole settings object, so every save sends all of it.
export const useSaveHubSpotSettings = () => {
  const queryClient = useQueryClient();
  // Returning the refetch keeps the save pending until the saved state shows.
  return useAPIMutation<
    { data: HubSpotFormsSettingsUpdate },
    HubSpotFormsSettings
  >(SETTINGS_KEY, { method: "PUT" }, () =>
    queryClient.invalidateQueries({ queryKey: [SETTINGS_KEY] })
  );
};
